import React from "react";
import { get, isEmpty } from "lodash";
import { useNavigate, useParams } from "helpers/tixxt-router";
import GroupForm from "./form";
import EditGroupActions from "./EditGroupActions";
import TixxtPageTitle from "../layout/TixxtPageTitle";
import { updateGroup, fetchGroup } from "./api";
import { useMutation, useQuery } from "react-query";
import { itemUrl, useLoadApp, useUpdateItem } from "../appCreator/api";
import { Group } from "../../@types";
import PageLoading from "components/shared/PageLoading";
import queryClient from "helpers/queryClient";

export default function EditGroup() {
  const navigate = useNavigate();
  const { groupSlug } = useParams();

  const { data: group } = useQuery(["group", groupSlug], () =>
    fetchGroup(groupSlug),
  );

  const networkId = Preload.current_network.id;
  const externalAppId = `network_${networkId}:group_category_${group?.group_category_id}:group_profile`;
  const { data: app, isLoading } = useLoadApp(
    { externalAppId },
    { enabled: !!group?.group_category_id },
  );

  const { mutate: onUpdateGroup } = useMutation(updateGroup, {
    onSuccess: (data, variables) => {
      toastr.success(
        I18n.t("js.groups.update.success", {
          group: get(variables, ["body", "group", "name"]),
        }),
      );
      navigate(`/groups/${data.groupSlug}/activitystreams/${data.groupSlug}`);
    },
    onError: () => {
      toastr.error(
        I18n.t("js.groups.update.failure", {
          group: group.name,
        }),
      );
    },
  });

  const { mutate: updateItem } = useUpdateItem({
    onSuccess: (item, { appId, externalAppId, itemId, externalItemId }) => {
      queryClient.setQueryData(
        itemUrl({ appId, externalAppId, itemId, externalItemId }),
        item,
      );
    },
  });

  const onSubmit = async (
    values: Group & { profile: { [key: string]: string | boolean } },
    {
      externalAppId,
      externalItemId,
    }: { externalAppId?: string; externalItemId?: string },
  ) => {
    const { profile, ...group } = values;

    updateItem({
      externalAppId,
      externalItemId,
      body: {
        values: profile,
      },
    });

    onUpdateGroup({ groupSlug, body: { group } });
  };

  if (!group) return <PageLoading />;

  return (
    <>
      <TixxtPageTitle />
      {!isEmpty(group) && (
        <>
          {!group.archived && (
            <GroupForm
              group={group}
              isAppLoading={isLoading}
              app={app}
              onSubmit={onSubmit}
            />
          )}
          {group.can.archive && <EditGroupActions {...group} />}
        </>
      )}
    </>
  );
}
